import { useState, type CSSProperties } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { deliveriesAPI } from '../../api/deliveries'
import type { Delivery, TimelineEntry } from '../../types'
import DashIcon from '../../components/ui/DashIcon'
import DashAvatar from '../../components/ui/DashAvatar'
import StatusBadge from '../../components/ui/StatusBadge'
import { ColisQRCode } from '../../components/expediteur/ColisQRCode'
import { ExpCancelModal } from '../../components/expediteur/modals/ExpCancelModal'
import { ExpCommentModal } from '../../components/expediteur/modals/ExpCommentModal'
import { ExpReclamationModal } from '../../components/expediteur/modals/ExpReclamationModal'
import Modal from '../../components/ui/Modal'
import { OffersPanel } from '../../components/expediteur/OffersPanel'

// ─── Constants ─────────────────────────────────────────────────────────────────

const TRACKING_STEPS = [
  { statuses: ['pending', 'validated', 'matching'], label: 'Commande créée',                pending: 'Création de la commande' },
  { statuses: ['accepted', 'assigned'],              label: 'Livreur trouvé',                pending: 'En attente d\'un livreur' },
  { statuses: ['picked_up'],                         label: 'Récupéré par le livreur',       pending: 'Ramassage à planifier' },
  { statuses: ['in_transit'],                        label: 'En route vers le destinataire', pending: 'Transit en cours' },
  { statuses: ['delivered'],                         label: 'Livré',                         pending: 'Signature + photo requis' },
]

const STATUS_LABEL: Record<string, string> = {
  pending: 'Commande créée', validated: 'Validé', matching: 'Matching livreur',
  accepted: 'Livreur trouvé', assigned: 'Livreur assigné', picked_up: 'Récupéré',
  in_transit: 'En route', delivered: 'Livré', failed: 'Échec de livraison',
  cancelled: 'Annulé', returned: 'Retourné', unreachable: 'Client injoinable',
}

const SIZE_LABEL: Record<string, string> = {
  small: 'Petit', medium: 'Moyen', large: 'Grand', extra: 'Extra-large',
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })

const fmtRelative = (iso: string) => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 1) return 'à l\'instant'
  if (mins < 60) return `il y a ${mins} min`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `il y a ${hrs}h`
  return `il y a ${Math.floor(hrs / 24)}j`
}

function stepIndexFor(status: string): number {
  for (let i = 0; i < TRACKING_STEPS.length; i++) {
    if (TRACKING_STEPS[i].statuses.includes(status)) return i
  }
  return -1
}

function timelineEntryFor(timeline: TimelineEntry[], statuses: string[]): TimelineEntry | undefined {
  return [...timeline].reverse().find(e => statuses.includes(e.status))
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function FieldRow({ label, value, sub, green, strong }: {
  label: string; value?: string | null; sub?: string | null; green?: boolean; strong?: boolean
}) {
  if (!value) return null
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      padding: '10px 0', borderBottom: '1px solid #F3F4F6',
    }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0, marginRight: 24, paddingTop: 2 }}>
        {label}
      </span>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 13, fontWeight: strong ? 700 : 500, color: green ? '#059669' : strong ? '#0D1117' : '#374151' }}>
          {value}
        </div>
        {sub && <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 1 }}>{sub}</div>}
      </div>
    </div>
  )
}

function InfoChip({ label, value }: { label: string; value?: string | null }) {
  return (
    <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: '10px 14px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#0D1117' }}>{value ?? '—'}</div>
    </div>
  )
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export const ExpediteurParcelDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: delivery, isLoading } = useQuery<Delivery>({
    queryKey: ['delivery', id],
    queryFn: () => deliveriesAPI.getById(id!),
    enabled: !!id,
    refetchInterval: 30_000,
  })

  const [showCancel,      setShowCancel]      = useState(false)
  const [showComment,     setShowComment]     = useState(false)
  const [showReclamation, setShowReclamation] = useState(false)
  const [showQR,          setShowQR]          = useState(false)

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['delivery', id] })
    queryClient.invalidateQueries({ queryKey: ['deliveries'] })
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <span style={{ color: '#9CA3AF', fontSize: 14 }}>Chargement…</span>
      </div>
    )
  }

  if (!delivery) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <DashIcon name="alert" size={32} style={{ color: '#EF4444' }} />
        <p style={{ color: '#EF4444', marginTop: 12 }}>Colis introuvable.</p>
        <button className="btn btn-ghost" onClick={() => navigate('/expediteur/livraisons')}>← Mes colis</button>
      </div>
    )
  }

  // ── Derived data ─────────────────────────────────────────────────────────────

  const livreur  = delivery.livreur
  const timeline = delivery.timeline ?? []

  const currentIdx = stepIndexFor(delivery.status)
  const isTerminal = ['delivered', 'cancelled', 'failed', 'returned', 'unreachable', 'return_pending', 'rescheduled'].includes(delivery.status)
  const isNegative = ['cancelled', 'failed', 'returned', 'unreachable', 'return_pending', 'rescheduled'].includes(delivery.status)
  const failureInfo = delivery.failureInfo
  const canCancel  = ['pending', 'matching', 'validated'].includes(delivery.status)
  const canClaim   = ['failed', 'cancelled'].includes(delivery.status)

  const assignedEntry    = timeline.find(e => ['accepted', 'assigned'].includes(e.status))
  const enServiceDepuis  = assignedEntry ? fmtTime(assignedEntry.timestamp) : '—'
  const vehicleLabel     = livreur?.vehicle?.type
    ? livreur.vehicle.type.charAt(0).toUpperCase() + livreur.vehicle.type.slice(1)
    : '—'

  const commission  = delivery.price?.commission   ?? Math.round((delivery.price?.base ?? 0) * 0.1 * 10) / 10

  return (
    <>
      {/* Modals */}
      {showCancel && (
        <ExpCancelModal deliveryId={delivery._id} trackingCode={delivery.trackingCode}
          onClose={() => setShowCancel(false)} onSuccess={invalidate} />
      )}
      {showComment && (
        <ExpCommentModal deliveryId={delivery._id}
          onClose={() => setShowComment(false)} onSuccess={invalidate} />
      )}
      {showReclamation && (
        <ExpReclamationModal deliveryId={delivery._id} trackingCode={delivery.trackingCode}
          onClose={() => setShowReclamation(false)} onSuccess={invalidate} />
      )}
      {showQR && (
        <Modal open title="QR Code du colis" onClose={() => setShowQR(false)} size="sm">
          <p style={{ textAlign: 'center', color: '#6B7280', fontSize: 13, marginBottom: 24, marginTop: 0 }}>
            Scannez ou imprimez pour coller sur le colis physique.
          </p>
          <ColisQRCode
            trackingCode={delivery.trackingCode}
            contactName={delivery.dropoff.contactName}
            dropoffAddress={delivery.dropoff.address}
            price={delivery.price?.base}
            paymentMethod={delivery.paymentMethod}
            size={200}
            showActions={true}
          />
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <button
              onClick={() => setShowQR(false)}
              style={{ padding: '10px 24px', borderRadius: 12, border: 'none', background: '#F3F4F6', color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Fermer
            </button>
          </div>
        </Modal>
      )}

      {/* ── Header ─────────────────────────────────────────────────────────────── */}
      <button
        onClick={() => navigate('/expediteur/livraisons')}
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 5, padding: '0 0 10px', fontFamily: 'inherit' }}
      >
        <DashIcon name="arrow-left" size={13} /> Mes colis
      </button>

      <div className="page-head" style={{ marginBottom: 20 }}>
        <div className="ph-titles">
          <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            Colis <span style={{ color: '#1D9E75' }}>{delivery.trackingCode}</span>
            <StatusBadge status={delivery.status} />
          </h1>
          <p style={{ margin: '5px 0 0', fontSize: 13, color: 'var(--muted)' }}>
            Créé le {fmtDate(delivery.createdAt)} {fmtTime(delivery.createdAt)}
            {delivery.updatedAt && ` · Mis à jour ${fmtRelative(delivery.updatedAt)}`}
          </p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-ghost" onClick={() => setShowQR(true)}>
            <DashIcon name="printer" size={14} /> QR Code
          </button>
          {canClaim && (
            <button className="btn btn-ghost" onClick={() => setShowReclamation(true)}>
              <DashIcon name="alert" size={14} /> Réclamation
            </button>
          )}
          {canCancel && (
            <button className="btn btn-ghost" style={{ color: '#DC2626', borderColor: '#FCA5A5' }} onClick={() => setShowCancel(true)}>
              <DashIcon name="x" size={14} /> Annuler
            </button>
          )}
        </div>
      </div>

      {/* ── Broadcast & Offres ─────────────────────────────────────────────────── */}
      <OffersPanel
        deliveryId={id ?? ''}
        status={delivery.status}
        region={(delivery as { region?: string }).region}
        onChanged={invalidate}
      />

      {/* ── Top row: QR + Suivi ────────────────────────────────────────────────── */}
      <div className="detail-grid" style={{ '--detail-cols': '230px 1fr', gap: 16, marginBottom: 16 } as CSSProperties}>

        {/* QR card */}
        <div style={{
          background: '#fff', border: '2px dashed #E5E7EB', borderRadius: 16, padding: 20,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
        }}>
          <ColisQRCode
            trackingCode={delivery.trackingCode}
            contactName={delivery.dropoff.contactName}
            dropoffAddress={delivery.dropoff.address}
            price={delivery.price?.base}
            paymentMethod={delivery.paymentMethod}
            size={160}
            showActions={false}
          />
          <div style={{ textAlign: 'center', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 3 }}>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 15, color: '#0D1117' }}>
                {delivery.trackingCode}
              </span>
              <button
                onClick={() => { navigator.clipboard.writeText(delivery.trackingCode); toast.success('Copié !') }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: 2, display: 'flex' }}
              >
                <DashIcon name="copy" size={13} />
              </button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>Code de suivi</div>
          </div>
          <div style={{ display: 'flex', gap: 8, width: '100%' }}>
            <button className="btn btn-ghost btn-sm" style={{ flex: 1, fontSize: 11, gap: 4 }} onClick={() => window.print()}>
              <DashIcon name="printer" size={12} /> Étiquette
            </button>
            <button className="btn btn-ghost btn-sm" style={{ flex: 1, fontSize: 11, gap: 4 }} onClick={() => setShowQR(true)}>
              <DashIcon name="download" size={12} /> QR PDF
            </button>
          </div>
        </div>

        {/* Suivi du colis */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#0D1117' }}>Suivi du colis</span>
            {!isTerminal && (
              <span style={{
                background: '#EFF9F5', border: '1px solid #A7F3D0', color: '#059669',
                fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20,
              }}>
                En cours
              </span>
            )}
          </div>

          {isNegative ? (
            <div style={{ padding: '16px 20px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 12, color: '#DC2626' }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{STATUS_LABEL[delivery.status]}</div>
              {delivery.cancellation?.reason && (
                <p style={{ margin: '6px 0 0', fontSize: 13, color: '#6B7280', fontWeight: 400 }}>
                  {delivery.cancellation.reason}
                </p>
              )}
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: 11, top: 20, bottom: 20, width: 2, background: '#E5E7EB', zIndex: 0 }} />
              {TRACKING_STEPS.map((step, idx) => {
                const isDone   = currentIdx > idx
                const isActive = currentIdx === idx
                const entry    = timelineEntryFor(timeline, step.statuses)
                return (
                  <div key={idx} style={{ display: 'flex', gap: 14, marginBottom: 20, position: 'relative', zIndex: 1 }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isDone ? '#1D9E75' : isActive ? '#1D9E75' : '#F3F4F6',
                      border: !isDone && !isActive ? '2px solid #E5E7EB' : 'none',
                      boxShadow: isActive ? '0 0 0 4px rgba(29,158,117,0.18)' : 'none',
                      fontSize: 11, color: '#fff', fontWeight: 700,
                    }}>
                      {isDone ? '✓' : isActive ? <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} /> : null}
                    </div>
                    <div style={{ paddingTop: 2, flex: 1 }}>
                      <div style={{ fontWeight: isDone || isActive ? 700 : 400, fontSize: 13, color: isDone || isActive ? '#0D1117' : '#9CA3AF', marginBottom: 2 }}>
                        {step.label}
                      </div>
                      {entry?.note && <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 1 }}>{entry.note}</div>}
                      {entry
                        ? <div style={{ fontSize: 11, color: '#9CA3AF' }}>{fmtDate(entry.timestamp)} · {fmtTime(entry.timestamp)}</div>
                        : <div style={{ fontSize: 11, color: '#C4C9D4', fontStyle: 'italic' }}>{step.pending}</div>
                      }
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Historique des tentatives (visible si échec) ──────────────────────── */}
      {['failed', 'rescheduled', 'return_pending', 'returned'].includes(delivery.status) && failureInfo && (
        <div style={{
          background: '#fff', border: '1px solid #E5E7EB', borderLeft: '4px solid #EF4444',
          borderRadius: 16, padding: 24, marginBottom: 16,
        }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#0D1117' }}>
            Historique des tentatives
          </h3>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            {Array.from({ length: failureInfo.maxAttempts ?? 3 }).map((_, i) => (
              <div key={i} style={{
                flex: 1, height: 8, borderRadius: 4,
                background: i < (failureInfo.attempts ?? 0) ? '#EF4444' : '#E5E7EB',
              }} />
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6B7280' }}>Tentatives</span>
              <strong style={{ color: (failureInfo.attempts ?? 0) >= (failureInfo.maxAttempts ?? 3) ? '#DC2626' : '#0D1117' }}>
                {failureInfo.attempts ?? 0} / {failureInfo.maxAttempts ?? 3}
              </strong>
            </div>
            {failureInfo.reasonLabel && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6B7280' }}>Dernier motif</span>
                <strong>{failureInfo.reasonLabel}</strong>
              </div>
            )}
            {failureInfo.note && (
              <div style={{ marginTop: 6, padding: '10px 12px', background: '#F9FAFB', borderRadius: 8, fontStyle: 'italic', color: '#374151' }}>
                "{failureInfo.note}"
              </div>
            )}
            {failureInfo.rescheduledFor && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6B7280' }}>Reprogrammé pour</span>
                <strong>{new Date(failureInfo.rescheduledFor).toLocaleDateString('fr-TN', { day: '2-digit', month: 'long' })}</strong>
              </div>
            )}
            {failureInfo.adminDecision && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6B7280' }}>Décision admin</span>
                <strong>{{
                  reschedule: 'Reprogrammation',
                  return:     'Retour demandé',
                  abandon:    'Classé sans suite',
                }[failureInfo.adminDecision] ?? '—'}</strong>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Middle row: Détails + Livreur ─────────────────────────────────────── */}
      <div className="detail-grid" style={{ '--detail-cols': '1fr 380px', gap: 16, marginBottom: 16 } as CSSProperties}>

        {/* Détails du colis */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16, padding: 24 }}>
          <h3 style={{ margin: '0 0 18px', fontSize: 14, fontWeight: 700, color: '#0D1117' }}>Détails du colis</h3>

          {/* Route visual */}
          <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 12, padding: '16px 18px', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, marginTop: 3 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#1D9E75', border: '2px solid #059669' }} />
                <div style={{ width: 2, height: 32, marginTop: 4, background: 'repeating-linear-gradient(to bottom, #9CA3AF 0, #9CA3AF 4px, transparent 4px, transparent 8px)' }} />
              </div>
              <div style={{ paddingBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#1D9E75', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Départ</div>
                <div style={{ fontSize: 13, color: '#374151' }}>{delivery.pickup?.address ?? '—'}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flexShrink: 0, marginTop: 3 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#0D1117', border: '2px solid #374151' }} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#0D1117', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                  Destination · {delivery.dropoff.contactName ?? '—'}
                </div>
                <div style={{ fontSize: 13, color: '#374151' }}>{delivery.dropoff.address}</div>
              </div>
            </div>
          </div>

          <FieldRow label="Destinataire" value={delivery.dropoff.contactName ?? '—'} sub={delivery.dropoff.contactPhone} />
          {delivery.package?.weight && <FieldRow label="Poids" value={`${delivery.package.weight} kg`} />}
          <FieldRow label="Type" value={[
            delivery.package?.fragile ? 'Fragile' : null,
            delivery.package?.description,
          ].filter(Boolean).join(' · ') || SIZE_LABEL[delivery.package?.size ?? 'medium']} />
          {delivery.paymentMethod === 'cash' && (
            <FieldRow label="COD" value={`${delivery.price?.base ?? 0} DT`} green strong />
          )}
          <FieldRow label="Commission" value={`${commission} DT (10%)`} />
          {delivery.comment && <FieldRow label="Note" value={`« ${delivery.comment} »`} />}
          {delivery.dropoff.notes && <FieldRow label="Note livraison" value={delivery.dropoff.notes} />}

          {/* Tracking link */}
          <div style={{ marginTop: 14, padding: '10px 14px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div>
              <div style={{ fontSize: 11, color: '#059669', fontWeight: 600 }}>Lien de suivi</div>
              <div style={{ fontSize: 12, color: '#374151', fontFamily: 'monospace' }}>
                traiqgo.tn/track/{delivery.trackingCode}
              </div>
            </div>
            <button
              onClick={() => { navigator.clipboard.writeText(`https://traiqgo.tn/track/${delivery.trackingCode}`); toast.success('Lien copié !') }}
              style={{ background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}
            >
              Copier
            </button>
          </div>
        </div>

        {/* Livreur assigné */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0D1117' }}>Livreur assigné</h3>
            {livreur?.name && (
              <span style={{
                display: 'flex', alignItems: 'center', gap: 5,
                background: '#EFF9F5', border: '1px solid #A7F3D0', color: '#059669',
                fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981', display: 'inline-block' }} />
                En service
              </span>
            )}
          </div>

          {!livreur?.name ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#9CA3AF' }}>
              <DashIcon name="user" size={32} />
              <p style={{ margin: '10px 0 0', fontSize: 13, fontStyle: 'italic' }}>Aucun livreur assigné</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 20 }}>
                <DashAvatar name={livreur.name} size={48} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#0D1117', marginBottom: 3 }}>{livreur.name}</div>
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>
                    {livreur._id ? `LV-${String(livreur._id).slice(-4).toUpperCase()}` : ''}
                  </div>
                  {(livreur.rating ?? 0) > 0 && (
                    <div style={{ fontSize: 13, color: '#374151', display: 'flex', gap: 4, alignItems: 'center' }}>
                      <span style={{ color: '#F59E0B' }}>★</span>
                      <span style={{ fontWeight: 600 }}>{(livreur.rating ?? 0).toFixed(1)}</span>
                      <span style={{ color: '#9CA3AF' }}>· {livreur.totalDeliveries ?? 0} livraisons</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="detail-grid" style={{ '--detail-cols': '1fr 1fr', gap: 8, marginBottom: 20 } as CSSProperties}>
                <InfoChip label="Véhicule"          value={vehicleLabel} />
                <InfoChip label="Plaque"             value={livreur.vehicle?.plate ?? '—'} />
                <InfoChip label="Téléphone"          value={livreur.phone ?? '—'} />
                <InfoChip label="En service depuis"  value={enServiceDepuis} />
              </div>

              <div className="detail-grid" style={{ '--detail-cols': '1fr 1fr', gap: 8 } as CSSProperties}>
                <a
                  href={`tel:${livreur.phone}`}
                  className="btn btn-ghost"
                  style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <DashIcon name="phone" size={14} /> Appeler
                </a>
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    if (!livreur.phone) return
                    const p = livreur.phone.replace(/\D/g, '')
                    window.open(`https://wa.me/${p}?text=${encodeURIComponent(`Bonjour, concernant le colis ${delivery.trackingCode}.`)}`, '_blank')
                  }}
                >
                  <DashIcon name="message" size={14} /> WhatsApp
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Actions ───────────────────────────────────────────────────────────── */}
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16, padding: 24 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#0D1117' }}>Actions</h3>
        <p style={{ margin: '0 0 20px', fontSize: 12, color: '#9CA3AF' }}>Gestion et suivi de votre colis.</p>
        <div className="detail-grid" style={{ '--detail-cols': '1fr 1fr', gap: 10 } as CSSProperties}>
          {([
            { icon: 'message', label: 'Commenter',         onClick: () => setShowComment(true) },
            { icon: 'printer', label: 'QR Code',           onClick: () => setShowQR(true) },
            ...(canClaim ? [{ icon: 'alert',  label: 'Réclamation',      onClick: () => setShowReclamation(true) }] : []),
            ...(canCancel ? [{ icon: 'x',     label: 'Annuler le colis', onClick: () => setShowCancel(true), danger: true }] : []),
            ...(isTerminal && !canClaim ? [{ icon: 'check', label: 'Colis clôturé', onClick: () => {}, disabled: true }] : []),
          ] as { icon: string; label: string; onClick: () => void; danger?: boolean; disabled?: boolean }[]).map((a) => (
            <button
              key={a.label}
              onClick={a.onClick}
              disabled={a.disabled}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px',
                borderRadius: 12, border: `1px solid ${a.danger ? '#FCA5A5' : '#E5E7EB'}`,
                background: a.danger ? '#FEF2F2' : '#F9FAFB',
                color: a.danger ? '#DC2626' : a.disabled ? '#9CA3AF' : '#374151',
                fontSize: 13, fontWeight: 600,
                cursor: a.disabled ? 'not-allowed' : 'pointer',
                opacity: a.disabled ? 0.6 : 1,
                fontFamily: 'inherit', textAlign: 'left',
              }}
            >
              <DashIcon name={a.icon} size={15} />
              {a.label}
            </button>
          ))}
        </div>
      </div>
    </>
  )
}
